import type { VotingDetail } from '../../../../api/DigiobcanApi';
import {
  DEFAULT_SCALE_MAX,
  DEFAULT_SCALE_MIN,
  type QuestionType,
} from '../../../../lib/votings';

/** Kto smie hlasovať. Na drôte je to dvojica booleanov, v UI jeden výber. */
export type VotingAudience = 'anyone' | 'authenticated' | 'verified';

export type QuestionValues = {
  title: string;
  description?: string;
  minOptions: number;
  maxOptions: number;
  hasFreeQuestion: boolean;
  questionType: QuestionType;
  /** Len pre `scale`; odchod z toho typu ich musí vyčistiť. */
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  freeQuestionHasUrl?: boolean;
  freeQuestionUrl?: string;
  /**
   * Pri `scale` sú to **tvrdenia**, nie možnosti na výber.
   *
   * `optional` je vo formulári obrátené oproti API (`required`) zámerne:
   * zaškrtávacie políčko má prázdny stav znamenať predvolené správanie, a tým
   * je „povinné". Preklad je v `toApiValues`/`toFormValues` a nikde inde.
   */
  options: {
    title: string;
    description?: string;
    url?: string;
    hasUrl?: boolean;
    optional?: boolean;
  }[];
};

export type FormValues = {
  title: string;
  description: string;
  openFrom: Date | null;
  openTo: Date | null;
  audience: VotingAudience;
  public: boolean;
  /**
   * Dvojica o výsledkoch je **výhybka, nie dva nezávislé prepínače**:
   * `availableResults` hovorí, či ich občan uvidí vôbec, a až pod ním má
   * `availableIncompleteResults` zmysel (uvidí ich už počas hlasovania).
   * Vypnutý prvý druhý zhasne - vo formulári aj v `toApiValues`, lebo tú
   * kombináciu odmietne CHECK v databáze.
   */
  availableResults: boolean;
  availableIncompleteResults: boolean;
  /**
   * Odoslať po uzavretí report na `reportEmails`. Je to **rovnaká výhybka**
   * ako pri výsledkoch: zapnuté bez adries server odmietne
   * (`VOTING_REPORT_NO_RECIPIENTS`) a CHECK v databáze tú kombináciu nepustí,
   * takže formulár nesmie dovoliť ju odoslať.
   */
  sendReport: boolean;
  reportEmails: string[];
  questions: QuestionValues[];
};

export type ApiFormValues = {
  title: string;
  description: string;
  openFrom: Date | null;
  openTo: Date | null;
  verifiedAccountsOnly: boolean;
  allowAnonymous: boolean;
  public: boolean;
  availableResults: boolean;
  availableIncompleteResults: boolean;
  sendReport: boolean;
  reportEmails: string[];
  questions: {
    title: string;
    description?: string;
    type: QuestionType;
    minOptions: number;
    maxOptions: number;
    scaleMin?: number;
    scaleMax?: number;
    scaleMinLabel?: string;
    scaleMaxLabel?: string;
    options: {
      title: string;
      description?: string;
      required?: boolean;
      url?: string;
      type: 'choice' | 'free';
    }[];
  }[];
};

/** Prázdna otázka zvoleného typu - „Pridať otázku" aj prvá otázka z 4b. */
export const emptyQuestion = (questionType: QuestionType): QuestionValues => {
  const base = {
    title: '',
    hasFreeQuestion: false,
    questionType,
  };

  if (questionType === 'scale') {
    return {
      ...base,
      minOptions: 1,
      maxOptions: 1,
      scaleMin: DEFAULT_SCALE_MIN,
      scaleMax: DEFAULT_SCALE_MAX,
      options: [{ title: '' }],
    };
  }

  if (questionType === 'free_text') {
    return {
      ...base,
      minOptions: 1,
      maxOptions: 1,
      hasFreeQuestion: true,
      options: [],
    };
  }

  return {
    ...base,
    minOptions: 1,
    maxOptions: questionType === 'multi_choice' ? 2 : 1,
    options: [
      { title: '', hasUrl: false },
      { title: '', hasUrl: false },
    ],
  };
};

/**
 * Otázka prepnutá do iného typu.
 *
 * So štyrmi typmi je prechod matica 4×4, tak sa to nepíše po pároch, ale ako
 * normalizácia **do cieľového typu**: „takto vyzerá otázka typu X" dostane
 * doterajší stav a vytiahne z neho, čo sa dá.
 *
 * Pravidlo, ktoré to drží: **napísaný text prežije prepnutie typu vždy,
 * štruktúra nikdy.** Konkrétne
 *
 *  * `title`, `description` a texty možností prežijú všetko;
 *  * `url` / `hasUrl` prežijú len medzi dvomi výbermi - tvrdenia škály ani
 *    otvorená odpoveď odkazy nemajú;
 *  * `single_choice` ↔ `multi_choice` je bezstratové, mení sa len `min/max`;
 *  * odchod zo `scale` **musí** vyčistiť `scaleMax` aj popisy, inak by
 *    zabudnutá hodnota prešla do payloadu a server ju odmietne ako
 *    `VOTING_SCALE_NOT_ALLOWED`;
 *  * doplnenie na minimum: škála aspoň jedno tvrdenie, výber aspoň dve možnosti.
 */
export const retypeQuestion = (
  current: QuestionValues,
  questionType: QuestionType,
  values: {
    minOptions?: number;
    maxOptions?: number;
    scaleMin?: number;
    scaleMax?: number;
    scaleMinLabel?: string;
    scaleMaxLabel?: string;
  } = {},
): QuestionValues => {
  const titles = current.options.map(({ title }) => title);
  const scaleless = {
    scaleMin: undefined,
    scaleMax: undefined,
    scaleMinLabel: undefined,
    scaleMaxLabel: undefined,
  };

  if (questionType === 'scale') {
    return {
      ...current,
      ...values,
      questionType,
      scaleMin: values.scaleMin ?? current.scaleMin ?? DEFAULT_SCALE_MIN,
      scaleMax: values.scaleMax ?? current.scaleMax ?? DEFAULT_SCALE_MAX,
      hasFreeQuestion: false,
      freeQuestionHasUrl: false,
      freeQuestionUrl: undefined,
      // Text tvrdenia aj jeho popis prežijú, `optional` tiež - je to vlastnosť
      // tvrdenia, nie štruktúry, a pri návrate do škály by sa inak ticho
      // stratila.
      options: (titles.length ? titles : ['']).map((title, i) => ({
        title,
        description: current.options[i]?.description,
        optional: current.options[i]?.optional,
      })),
    };
  }

  if (questionType === 'free_text') {
    return {
      ...current,
      ...values,
      ...scaleless,
      questionType,
      minOptions: 1,
      maxOptions: 1,
      hasFreeQuestion: true,
      options: [],
    };
  }

  const wasChoice =
    current.questionType === 'single_choice' ||
    current.questionType === 'multi_choice';
  const options = (
    titles.length >= 2 ? titles : [...titles, '', ''].slice(0, 2)
  ).map((title, i) => ({
    title,
    description: current.options[i]?.description,
    // `optional` je vlastnosť tvrdenia škály; poslať ho pri výbere je 400
    // (`VOTING_REQUIRED_NOT_ALLOWED`), takže odchod zo škály ho musí zmazať -
    // rovnaký dôvod, pre aký `scaleless` čistí `scaleMax`.
    optional: undefined,
    // Odkaz prežije len medzi výbermi; inak by sa tvrdeniu pripísal odkaz,
    // ktorý mu formulár ani neukáže.
    ...(wasChoice
      ? {
          url: current.options[i]?.url,
          hasUrl: current.options[i]?.hasUrl,
        }
      : {}),
  }));

  const maxCandidate = values.maxOptions ?? current.maxOptions;

  return {
    ...current,
    ...values,
    ...scaleless,
    questionType,
    minOptions:
      questionType === 'single_choice'
        ? 1
        : Math.min(
            values.minOptions ?? current.minOptions ?? 1,
            options.length,
          ),
    maxOptions:
      questionType === 'single_choice'
        ? 1
        : Math.min(Math.max(maxCandidate ?? 1, 1), options.length),
    // Voľná odpoveď „Iné" prežije prechod medzi výbermi, ale nie príchod
    // z `free_text`, kde bola samotnou odpoveďou.
    hasFreeQuestion: wasChoice ? current.hasFreeQuestion : false,
    options,
  };
};

/** Detail z API do počiatočných hodnôt formulára. */
export const toFormValues = (voting?: VotingDetail): FormValues => ({
  title: voting?.title || '',
  description: voting?.description || '',
  audience: voting?.allow_anonymous
    ? 'anyone'
    : voting?.verified_accounts_only
      ? 'verified'
      : 'authenticated',
  public: voting?.public !== undefined ? voting.public : true,
  // Nové hlasovanie výsledky zverejňuje; skryť ich je aktívne rozhodnutie.
  availableResults:
    voting?.available_results !== undefined ? voting.available_results : true,
  availableIncompleteResults: voting?.available_incomplete_results || false,
  sendReport: voting?.report_enabled || false,
  reportEmails: voting?.report_emails ?? [],
  openFrom: voting?.open_from ? new Date(voting.open_from) : null,
  openTo: voting?.open_to ? new Date(voting.open_to) : null,
  questions:
    voting?.questions.map((q) => {
      const freeOption = q.options.find((o) => o.type === 'free');
      return {
        title: q.title,
        description: q.description,
        minOptions: q.min_options,
        maxOptions: q.max_options,
        // Pri `free_text` je voľná možnosť samotná odpoveď, nie „Iné".
        hasFreeQuestion:
          q.type !== 'free_text' && q.options.some((o) => o.type === 'free'),
        freeQuestionHasUrl: !!freeOption?.url,
        freeQuestionUrl: freeOption?.url || undefined,
        // Typ už nesie API - derivácia oňuchávaním možností tu bola len preto,
        // že ho kontrakt nemal.
        questionType: q.type,
        scaleMin: q.scale_min ?? undefined,
        scaleMax: q.scale_max ?? undefined,
        scaleMinLabel: q.scale_min_label ?? undefined,
        scaleMaxLabel: q.scale_max_label ?? undefined,
        options: q.options
          .filter((o) => o.type !== 'free')
          .map((o) => ({
            title: o.title,
            description: o.description || undefined,
            url: o.url,
            hasUrl: !!o.url,
            // `required !== false` je „povinné" vrátane NULL z riadkov spred
            // migrácie 000021 - tá istá derivácia, akú má server.
            optional: o.required === false,
          })),
      };
    }) ?? [],
});

/**
 * Formulárový tvar do tela požiadavky.
 *
 * Trojstavový výber publika sa rozkladá na dvojicu booleanov, ktorú nesie API.
 * Kombináciu „aj neprihlásení + len overené" server odmietne, ale tu vzniknúť
 * nemôže - výber je jeden.
 */
export const toApiValues = ({
  audience,
  ...values
}: FormValues): ApiFormValues => ({
  ...values,
  verifiedAccountsOnly: audience === 'verified',
  allowAnonymous: audience === 'anyone',
  // Bez `public` by neprihlásený hlasovanie ani nevidel, takže „ktokoľvek"
  // ho vynucuje. Rovnaké pravidlo popisuje hint vo formulári.
  public: audience === 'anyone' ? true : values.public,
  // To isté pravidlo ako vo formulári: nezverejnené výsledky nemôžu byť
  // priebežné. Bez tohto by vypnutie prvého prepínača nechalo v tele
  // požiadavky kombináciu, ktorú server aj tak zrovná.
  availableIncompleteResults:
    values.availableResults && values.availableIncompleteResults,
  // Vypnutý report nemá komu chodiť, takže sa zoznam adries zahodí - inak by
  // sa v koncepte skryto viezol ďalej a po opätovnom zapnutí by report odišiel
  // niekomu, o kom už úradník nevie.
  reportEmails: values.sendReport ? values.reportEmails : [],
  questions: values.questions.map((q) => {
    const base = {
      title: q.title,
      description: q.description || undefined,
      type: q.questionType,
    };

    if (q.questionType === 'scale') {
      // Tvrdenia nemajú odkazy ani voľnú odpoveď. `min/max` server aj tak
      // prepíše na počet tvrdení, posielame ich len preto, že sú povinné.
      return {
        ...base,
        // `min` prepíše server na počet povinných tvrdení, `max` na ich počet;
        // posielame ich len preto, že sú v kontrakte povinné.
        minOptions: q.options.length,
        maxOptions: q.options.length,
        scaleMin: q.scaleMin ?? DEFAULT_SCALE_MIN,
        scaleMax: q.scaleMax ?? DEFAULT_SCALE_MAX,
        scaleMinLabel: q.scaleMinLabel || undefined,
        scaleMaxLabel: q.scaleMaxLabel || undefined,
        options: q.options.map(({ title, description, optional }) => ({
          title,
          description: description || undefined,
          required: !optional,
          type: 'choice' as const,
        })),
      };
    }

    if (q.questionType === 'free_text') {
      return {
        ...base,
        minOptions: 1,
        maxOptions: 1,
        options: [
          {
            title: '',
            type: 'free' as const,
            ...(q.freeQuestionHasUrl && q.freeQuestionUrl
              ? { url: q.freeQuestionUrl }
              : {}),
          },
        ],
      };
    }

    return {
      ...base,
      minOptions: q.questionType === 'single_choice' ? 1 : q.minOptions,
      maxOptions: q.questionType === 'single_choice' ? 1 : q.maxOptions,
      options: [
        ...q.options.map(({ title, description, hasUrl, url }) => ({
          title,
          ...(description ? { description } : {}),
          type: 'choice' as const,
          ...(hasUrl && url ? { url } : {}),
        })),
        // Voliteľná možnosť „Iné" s textovým poľom. Nie je to typ otázky -
        // je to príznak možnosti a smie stáť pri oboch výberoch.
        ...(q.hasFreeQuestion
          ? [
              {
                title: '',
                type: 'free' as const,
                url: q.freeQuestionHasUrl ? q.freeQuestionUrl : undefined,
              },
            ]
          : []),
      ],
    };
  }),
});

/**
 * Otázka vo formulári v tvare, ktorému rozumejú spoločné funkcie nad API
 * tvarom (`questionSummary`). Builder pracuje s `questionType`/`options.title`,
 * zdieľané funkcie s `type`/`options.type` - toto je most medzi nimi.
 */
export const questionShape = (question: QuestionValues) => ({
  type: question.questionType,
  min_options: question.minOptions,
  max_options: question.maxOptions,
  scale_min: question.scaleMin,
  scale_max: question.scaleMax,
  options: [
    ...question.options.map((option) => ({
      type: 'choice' as const,
      required: !option.optional,
    })),
    ...(question.hasFreeQuestion ? [{ type: 'free' as const }] : []),
  ],
});
